import { AfterViewInit, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatLineModule } from '@angular/material/core';
import { firstValueFrom, isObservable, Observable, Subscription } from 'rxjs';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatRadioModule } from '@angular/material/radio';
import { MatToolbarModule } from '@angular/material/toolbar';
import { AutotrimDirective, DialogFormActionsComponent } from '../../common';
import { Company, CompanySite, Document, DocumentType } from '../../model';
import { DocumentService, TranslationService } from '..';
import { TranslateModule } from '@ngx-translate/core';
import { formatJsonDate } from '../../util';

interface UploadStatus {
  subscription: Subscription;
  name: string;
  progress: number;
  error?: string;
}

/** Optional secondary file slot — rendered ALWAYS VISIBLE when configured, never required. */
export interface SecondaryFileSlot {
  label: string;
  hint: string;
}

/**
 * Config-gated alternate mode: a custom upload handler replacing the generic `POST /api/document`
 * call entirely, plus an optional always-visible secondary file. Without a mode the caller gets
 * the original generic dialog behaviour, byte-for-byte.
 */
export interface DocumentUploadMode<R = Document> {
  secondaryFile?: SecondaryFileSlot;
  uploadHandler?: (file: File, secondaryFile?: File) => Observable<R> | Promise<R>;
}

@Component({
  selector: 'app-document-upload-dialog',
  templateUrl: 'document-upload-dialog.component.html',
  styleUrls: ['document-upload-dialog.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatRadioModule,
    MatIconModule,
    MatDatepickerModule,
    MatInputModule,
    MatLineModule,
    MatButtonModule,
    MatProgressBarModule,
    MatToolbarModule,
    DialogFormActionsComponent,
    AutotrimDirective,
    TranslateModule,
  ],
})
export class DocumentUploadDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly cdr = inject(ChangeDetectorRef);
  dialogRef = inject<MatDialogRef<DocumentUploadDialogComponent>>(MatDialogRef);
  private readonly service = inject(DocumentService);
  private readonly translate = inject(TranslationService);
  private readonly data = inject(MAT_DIALOG_DATA) as {
    title: string;
    types: DocumentType[];
    company?: Company;
    site?: CompanySite;
    file?: File;
    secondaryFile?: SecondaryFileSlot;
    uploadHandler?: (file: File, secondaryFile?: File) => Observable<unknown> | Promise<unknown>;
  };

  title!: string;
  uploadStatus?: UploadStatus;
  uploadResult?: Document;
  fileInput!: HTMLInputElement;
  company?: Company;
  site?: CompanySite;
  private initialFile?: File;
  type?: string;
  types!: DocumentType[];
  form = new FormBuilder().nonNullable.group({
    type: ['', Validators.required],
    date: [null as Date | null, Validators.required],
    title: ['', Validators.required],
  });
  step: 1 | 2 = 1;

  /** True when `data.uploadHandler` replaces the generic upload — gates the whole template branch. */
  isCustomMode = false;
  secondaryFileConfig: SecondaryFileSlot | null = null;
  private uploadHandlerFn?: (file: File, secondaryFile?: File) => Observable<unknown> | Promise<unknown>;
  selectedFile: File | null = null;
  selectedSecondaryFile: File | null = null;
  customBusy = false;
  customError?: string;

  async ngOnInit() {
    this.dialogRef.afterClosed().subscribe(async (result) => {
      if (!result) {
        try {
          await this.removeUpload();
        } catch (err) {
          console.log(err);
        }
      }
    });
    this.title = this.data.title;
    this.company = this.data.company;
    this.site = this.data.site;
    this.initialFile = this.data.file;
    this.secondaryFileConfig = this.data.secondaryFile ?? null;
    this.uploadHandlerFn = this.data.uploadHandler;
    this.isCustomMode = !!this.uploadHandlerFn;

    if (this.isCustomMode) {
      // Custom mode never builds a generic Document (type/date/title are meaningless here).
      this.types = this.data.types ?? [];
      return;
    }

    this.types = this.data.types;
    this.form.patchValue({ type: this.types.length ? this.types[0].id : undefined });
  }

  ngAfterViewInit(): void {
    this.resetFileInput();
  }

  ngOnDestroy() {
    this.uploadStatus?.subscription?.unsubscribe();
    this.fileInput?.remove();
  }

  async removeUpload() {
    this.uploadStatus?.subscription?.unsubscribe();
    this.uploadStatus = undefined;

    if (this.uploadResult) {
      try {
        await this.service.delete(this.uploadResult);
      } catch (err) {
        console.log(err);
      }
      this.uploadResult = undefined;
    }
    this.resetFileInput();
  }

  resetFileInput() {
    // Custom mode drives its own template-ref file inputs — the dynamically created one is unused.
    if (this.isCustomMode) return;
    this.fileInput?.remove();
    const input = (this.fileInput = document.createElement('input'));
    input.type = 'file';
    input.onchange = () => {
      const fileObj = input.files && input.files.length ? input.files[0] : undefined;
      if (fileObj) {
        this.beginUpload(fileObj);
      }
    };
    input.style.display = 'none';
    document.body.append(input);
    this.cdr.detectChanges();
  }

  async save() {
    if (this.uploadResult) {
      const { date, type, title } = this.form.value as { date: Date; type: string; title: string };
      const typeInfo = this.types.find(({ id }) => id === type);
      await this.service.update(this.uploadResult.id, {
        ...this.uploadResult,
        title,
        type: { id: type, name: typeInfo?.name ?? (undefined as any) },
        date: formatJsonDate(date)!,
      });
      this.dialogRef.close({
        ...this.uploadResult,
        title,
        type: { id: type, name: typeInfo?.name ?? '' },
        date: formatJsonDate(date)!,
      });
    }
  }

  startUpload() {
    this.step = 2;
    if (this.initialFile) {
      this.beginUpload(this.initialFile);
      this.initialFile = undefined;
    } else {
      this.fileInput.click();
    }
  }

  private beginUpload(fileObj: File, metadata?: Record<string, any>) {
    const hasDot = fileObj.name.lastIndexOf('.');
    const defaultTitle = hasDot > 0 ? fileObj.name.substring(0, hasDot) : fileObj.name;
    const titleControl = this.form.controls.title;
    const existingTitle = titleControl.value?.trim();
    const uploadTitle = existingTitle ? existingTitle : defaultTitle;
    if (!existingTitle) {
      this.form.patchValue({ title: defaultTitle });
    }
    const upload: UploadStatus = (this.uploadStatus = {
      name: fileObj.name,
      progress: 0,
      subscription: this.service
        .upload(
          fileObj,
          this.form.value.type!,
          uploadTitle,
          (progress) => {
            upload.progress = progress;
            this.cdr.markForCheck();
          },
          metadata
        )
        .subscribe({
          next: (result) => {
            this.uploadResult = result;
            this.uploadStatus = undefined;
            this.form.patchValue({ date: new Date(result.date) });
            this.cdr.markForCheck();
          },
          error: async (error) => {
            // 409 duplicate_document → confirm dialog, retry with allowDuplicate (#148).
            // Handled here instead of via uploadWithDuplicateConfirm to keep the cancellable
            // subscription that removeUpload/ngOnDestroy rely on.
            if (await this.service.isDuplicateDocument(error)) {
              if (await this.service.confirmDuplicateUpload(error)) {
                this.beginUpload(fileObj, { ...metadata, allowDuplicate: true });
              } else {
                this.uploadStatus = undefined;
                this.step = 1;
              }
              this.cdr.markForCheck();
              return;
            }
            upload.error = await this.translate.loadFromBackend('upload_failed_try_again_tr');
            this.cdr.markForCheck();
          },
        }),
    });
  }

  // ---- custom mode (data.uploadHandler) ----

  onPrimaryFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.length ? input.files[0] : null;
    this.cdr.markForCheck();
  }

  onSecondaryFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.selectedSecondaryFile = input.files?.length ? input.files[0] : null;
    this.cdr.markForCheck();
  }

  async submitCustomUpload() {
    if (!this.selectedFile || !this.uploadHandlerFn || this.customBusy) return;
    this.customBusy = true;
    this.customError = undefined;
    this.cdr.markForCheck();
    try {
      const raw = this.uploadHandlerFn(this.selectedFile, this.selectedSecondaryFile ?? undefined);
      const result = await (isObservable(raw) ? firstValueFrom(raw) : raw);
      this.dialogRef.close(result);
    } catch (err) {
      console.log(err);
      this.customError = await this.translate.loadFromBackend('upload_failed_try_again_tr');
      this.customBusy = false;
      this.cdr.markForCheck();
    }
  }
}
